/** @license Copyright (c) Chamberlain Group. All rights reserved. */
/** @preserve version: 9.1.6 */

import bowser from 'bowser';
import createEnvironmentService from './utils/EnvironmentService.mjs';
import createUtil from './utils/Util.mjs';
import createApiService from './utils/ApiService.mjs';
import createLogService from './utils/LogService.mjs';
import createUserService from './users/UserService.mjs';
import createPaymentProcessorService from './users/PaymentProcessorService.mjs';
import createDeviceService from './devices/DeviceService.mjs';
import createFaceService from './events/FaceService.mjs';
import createEventFilter from './events/EventFilter.mjs';
import createEventService from './events/EventService.mjs';
import createEventDateService from './events/EventDateService.mjs';
import createVehicleService from './vehicles/VehicleService.mjs';
import createShareService from './devices/ShareService.mjs';
import createSchedulerService from './devices/SchedulerService.mjs';
import createMp4Util from './utils/Mp4Util.mjs';
import createServicesService from './devices/ServicesService.mjs';
import createMseVideoService from './media/MseVideoService.mjs';
import createHlsVideoService from './media/HlsVideoService.mjs';
import createAudioService from './media/AudioService.mjs';

/* eslint-disable */

/*
 * based on {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/find}
 */
if (!Array.prototype.findLast) {
    Object.defineProperty(Array.prototype, 'findLast', {
        value: function(predicate) {
            // 1. Let O be ? ToObject(this value).
            if (this == null) {
                throw TypeError('"this" is null or not defined');
            }

            var o = Object(this);

            // 2. Let len be ? ToLength(? Get(O, "length")).
            var len = o.length >>> 0;

            // 3. If IsCallable(predicate) is false, throw a TypeError exception.
            if (typeof predicate !== 'function') {
                throw TypeError('predicate must be a function');
            }

            // 4. If thisArg was supplied, let T be thisArg; else let T be undefined.
            var thisArg = arguments[1];

            // 5. Let k be 0.
            var k = len - 1;

            // 6. Repeat, while k < len
            while (k >= 0) {
                // a. Let Pk be ! ToString(k).
                // b. Let kValue be ? Get(O, Pk).
                // c. Let testResult be ToBoolean(? Call(predicate, T, « kValue, k, O »)).
                // d. If testResult is true, return kValue.
                var kValue = o[k];
                if (predicate.call(thisArg, kValue, k, o)) {
                    return kValue;
                }
                // e. Increase k by 1.
                k--;
            }

            // 7. Return undefined.
            return undefined;
        },
        configurable: true,
        writable: true
    });
}
/* eslint-enable */

const version = '9.1.6'; // The build replaces this with the actual version number
let sdk;
let util;
let deviceService;
let apiService;
let logService;
let userService;
let paymentProcessorService;
let faceService;
let eventFilter;
let eventService;
let eventDateService;
let vehicleService;
let shareService;
let schedulerService;
let servicesService;
let mp4Util;
let mseVideoService;
let hlsVideoService;
let audioService;
let environmentService;
let _window;

/**
 * @typedef Environment
 * @property {String} partnerId Examples: myQ, TendSecure. Used to customize properties and behavior for different apps.
 * @property {String} locale Examples: en-US, es-MX. Only used to set default clock format.
 * @property {String} apiUrlBase Full URL of the API root, or defaults to https://[thisDomain]/cxs/api.
 *     Should vary depending on environment. sessionId or oauthToken must also be set when using apiUrlBase.
 * @property {String} webUrlBase Full URL of the web resource root, or defaults to https://[thisDomain]/app. Should vary depending on environment.
 * @property {String} webSocketBase Base for WebSocket connections. Necessary for mseVideoService and audioService.
 *     defaults to wss://[thisDomain]:443. Should vary depending on environment.
 * @property {String} sessionId The current sessionId. sessionId or oauthToken must be set if apiUrlBase is set.
 * @property {String} oauthToken The curren oauth token. sessionId or oauthToken must be set if apiUrlBase is set.
 * @property {String} audioWorkerUrl Only necessary to use audioService when not on a Video Platform domain.
 *     defaults to https://[thisDomain]/app/lib/media/audioWorker.js
 * @property {String} libopusScriptUrl Only necessary to use audioService when not on a Video Platform domain.
 *     defaults to https://[thisDomain]/app/lib/media/libopus.wasm
 * @property {object} client information about the current browser. Read only and automatically calculated by the SDK.
 */

/**
 * @description Use this to initialize and access services in the videoSdk.
 *     The window and environment params
 *     must be provided on the first request only for the videoSdk.
 * @param {Window} window global browser execution context. Cannot be changed with subsequent calls to get.
 * @param {Environment} environment environment-specific customizations. Cannot be changed with subsequent calls to get.
 * @param {String} sessionId Required for non-Tend domains
 * @returns {VideoSdk} SDK instance that allows access to services
 */
function get(window, environment) {

    if (!_window) {
        if (!window) {
            throw new Error('missing or invalid window param when creating the videoSdk');
        }
        _window = window;
        _window.bowser = bowser;
    }

    function updateEnv(environment) {
        if (!environment) {
            throw new Error('missing or invalid environment param when creating the videoSdk');
        }
        let env = getEnvironmentService();
        // Need to do nothing if the property was not provided,
        // which is different than providing an undefined or null value.
        Object.keys(environment).forEach(key => {
            if (key === 'auth') {
                env.auth = environment.auth;
            }
            if (key === 'sessionId') {
                env.sessionId = environment.sessionId;
                if (!env.auth) {
                    env.auth = 'seedonkSession';
                }
            }
            if (key === 'oauthToken') {
                env.oauthToken = environment.oauthToken;
                if (!env.auth) {
                    env.auth = 'bearer';
                }
            }
            if (key === 'apiUrlBase') {
                env.apiUrlBase = environment.apiUrlBase;
            }
            if (key === 'webUrlBase') {
                env.webUrlBase = environment.webUrlBase;
            }
            if (key === 'webSocketBase') {
                env.webSocketBase = environment.webSocketBase;
            }
            if (key === 'partnerId') {
                env.partnerId = environment.partnerId;
            }
            if (key === 'locale') {
                env.locale = environment.locale;
            }
            if (key === 'audioWorkerUrl') {
                env.audioWorkerUrl = environment.audioWorkerUrl;
            }
            if (key === 'libopusScriptUrl') {
                env.libopusScriptUrl = environment.libopusScriptUrl;
            }
        });
        if (!environment.partnerId) {
            throw new Error('SDK must have a partnerId');
        }
        if (environment.apiUrlBase && !environment.sessionId && !environment.oauthToken) {
            throw new Error('SDK environment - when apiUrlBase is set, then sessionId or oauthToken must also be set');
        }
    }
    updateEnv(environment);

    /**
     * @private
     */
    function getUtil() {
        util ??= createUtil(
            _window
        );
        return util;
    }

    function getEnvironmentService() {
        environmentService ??= createEnvironmentService(
            getUtil(),
            _window
        );
        return environmentService;
    }

    function getLogService() {
        logService ??= createLogService(
            getUtil(),
            getEnvironmentService(),
            version,
            _window
        );
        return logService;
    }

    /**
     * @private
     */
    function getApiService() {
        apiService ??= createApiService(
            getLogService(),
            getUtil(),
            getEnvironmentService(),
            _window
        );
        return apiService;
    }

    function getUserService() {
        userService ??= createUserService(
            getApiService(),
            getLogService(),
            getEnvironmentService(),
            getUtil(),
            _window
        );
        return userService;
    }

    function getPaymentProcessorService() {
        paymentProcessorService ??= createPaymentProcessorService(
            getApiService()
        );
        return paymentProcessorService;
    }

    function getDeviceService() {
        deviceService ??= createDeviceService(
            getApiService(),
            getUserService(),
            getLogService(),
            getUtil(),
            _window,
            getEnvironmentService()
        );
        return deviceService;
    }

    function getFaceService() {
        faceService ??= createFaceService(
            getApiService(),
            getLogService(),
            getUtil(),
            _window
        );
        return faceService;
    }

    function getEventFilter() {
        eventFilter ??= createEventFilter(
            getUserService(),
            getDeviceService(),
            getLogService(),
            getUtil(),
            _window,
            getEnvironmentService()
        );
        return eventFilter;
    }

    function getEventService() {
        eventService ??= createEventService(
            getApiService(),
            getDeviceService(),
            getUserService(),
            getLogService(),
            getFaceService(),
            getEventFilter(),
            getEnvironmentService(),
            getUtil(),
            _window
        );
        return eventService;
    }

    function getEventDateService() {
        eventDateService ??= createEventDateService(
            getApiService(),
            getDeviceService(),
            getEventService(),
            getEventFilter(),
            getLogService(),
            getUtil(),
            _window
        );
        return eventDateService;
    }

    function getVehicleService() {
        vehicleService ??= createVehicleService(
            getApiService(),
            getLogService(),
            getUtil()
        );
        return vehicleService;
    }

    function getShareService() {
        shareService ??= createShareService(
            getApiService(),
            getDeviceService(),
            getLogService(),
            getUtil(),
            _window
        );
        return shareService;
    }

    function getSchedulerService() {
        schedulerService ??= createSchedulerService(
            getApiService(),
            getLogService(),
            getUtil(),
            _window
        );
        return schedulerService;
    }

    function getServicesService() {
        servicesService ??= createServicesService(
            getApiService(),
            getPaymentProcessorService(),
            getUtil()
        );
        return servicesService;
    }

    function getMp4Util() {
        mp4Util ??= createMp4Util(
            getLogService(),
            getUtil()
        );
        return mp4Util;
    }

    function getMseVideoService() {
        mseVideoService ??= createMseVideoService(
            getApiService(),
            getLogService(),
            getEnvironmentService(),
            _window,
            getUtil()
        );
        return mseVideoService;
    }

    function getHlsVideoService() {
        hlsVideoService ??= createHlsVideoService(
            getApiService(),
            getLogService(),
            getEnvironmentService(),
            _window,
            getUtil()
        );
        return hlsVideoService;
    }

    function getAudioService() {
        audioService ??= createAudioService(
            getApiService(),
            getLogService(),
            getEnvironmentService(),
            _window,
            getUtil()
        );
        return audioService;
    }

    sdk ??= Object.freeze({
        get deviceService() { return getDeviceService(); },
        get logService() { return getLogService(); },
        get faceService() { return getFaceService(); },
        get eventService() { return getEventService(); },
        get eventDateService() { return getEventDateService(); },
        get userService() { return getUserService(); },
        get vehicleService() { return getVehicleService(); },
        get shareService() { return getShareService(); },
        get schedulerService() { return getSchedulerService(); },
        get servicesService() { return getServicesService(); },
        get mp4Util() { return getMp4Util(); },
        get mseVideoService() { return getMseVideoService(); },
        get hlsVideoService() { return getHlsVideoService(); },
        get audioService() { return getAudioService(); },
        get version() { return version; },
        get environment() { return environmentService; },
        getTraceId: function() { return getUtil().randomBase64Code(15); }
    });

    /**
     * @typedef VideoSdk
     * @property {DeviceService} deviceService {@link module:DeviceService DeviceService}
     * @property {LogService} logService {@link module:LogService LogService}
     * @property {FaceService} faceService {@link module:FaceService FaceService}
     * @property {EventService} eventService {@link module:EventService EventService}
     * @property {EventDateService} eventDateService {@link module:EventDateService EventDateService}
     * @property {UserService} userService {@link module:UserService UserService}
     * @property {VehicleService} vehicleService {@link module:VehicleService VehicleService}
     * @property {ShareService} shareService {@link module:ShareService ShareService}
     * @property {SchedulerService} schedulerService {@link module:SchedulerService SchedulerService}
     * @property {ServicesService} servicesService {@link module:ServicesService ServicesService}
     * @property {Mp4Util} mp4Util {@link module:Mp4Util Mp4Util}
     * @property {MseVideoService} mseVideoService {@link module:MseVideoService MseVideoService}
     * @property {HlsVideoService} hlsVideoService {@link module:HlsVideoService HlsVideoService}
     * @property {AudioService} audioService {@link module:AudioService AudioService}
     * @property {String} version returns the version of the VideoSdk in semver format. Read only.
     * @property {Environment} environment environment values.
     * @property {String} getTraceId a function that returns a new cryptographically random string suitable for use as a traceId
     */

    return sdk;
}

export { get };
